// Ihsan Points + redemption — WIRED to real Supabase RPC 007_rewards_redemption.
//
// Catalogue (`listRewards`), atomic redemption (`redeemReward`) and history
// (`myRedemptions`) all go through PostgREST against the live project. The
// redeem call is idempotent by `client_key` — the UI generates one per attempt
// so a retry after a timeout doesn't double-charge.
//
// The pre-launch static export (REDEMPTION_REWARDS / IHSAN_POINTS) is kept for
// two reasons: `RewardsSheet` falls back to it if the catalogue fetch fails
// (schema warmup) and the top-bar preview before session hydrates.

import { rpc, POSTGREST_URL } from './rpc';
import { getAccessToken, SUPABASE_ANON } from './session';

// Fallback figure only — real balance flows from `profiles.points` via getMyProfile().
export const IHSAN_POINTS = 0;

// Rough point-to-SGD conversion for the "≈ S$" hint. 100 pts ≈ S$1.
export const POINT_VALUE_SGD = 0.01;

export function formatPoints(points = IHSAN_POINTS) {
  return Number(points || 0).toLocaleString('en-SG');
}

// Physical locations where a redemption can actually be used. Kept as a
// static list — this is a member-facing "where do I go" panel, not catalogue data.
export const REDEMPTION_PLACES = [
  {
    id: 'ihsan-hq',
    name: 'MTFA Darul Ihsan',
    detail: '31 Lorong 27 Geylang',
    note: 'Bursary top-ups, course fees'
  },
  {
    id: 'yishun-centre',
    name: 'Yishun Dialysis Centre',
    detail: 'Blk 846 Yishun Ring Road',
    note: 'Transport credits'
  },
  {
    id: 'henderson-centre',
    name: 'Henderson Dialysis Centre',
    detail: 'Blk 95 Henderson Road',
    note: 'Transport credits'
  },
  {
    id: 'madrasah-alsagoff',
    name: 'Madrasah Alsagoff',
    detail: '111 Jalan Sultan',
    note: 'Ulum Ihsan class materials'
  }
];

// Fallback catalogue — matches the seed rows in migration 007 so the sheet
// still looks right if the fetch fails before Supabase warms up.
export const REDEMPTION_REWARDS = [
  { id: 'kopi-at-centre',       title: 'Kopi at the centre',                cost: 100,  emoji: '☕' },
  { id: 'sedekah-convert-2sgd', title: 'Convert 200 pts into S$2 sedekah',  cost: 200,  emoji: '🤲' },
  { id: 'sedekah-convert-5sgd', title: 'Convert 500 pts into S$5 sedekah',  cost: 500,  emoji: '🤲' },
  { id: 'dialysis-transport',   title: 'Dialysis transport credit (S$5)',   cost: 500,  emoji: '🚐' },
  { id: 'course-credit-10sgd',  title: 'S$10 off a course fee',             cost: 1000, emoji: '📚' },
  { id: 'course-credit-25sgd',  title: 'S$25 off a course fee',             cost: 2500, emoji: '📚' }
];

// GET public.rewards (RLS: active rows only, granted to anon).
export async function listRewards() {
  const url = `${POSTGREST_URL}/rewards?select=id,title,description,cost_points,stock,active,emoji,category,sort_order&active=eq.true&order=sort_order.asc`;
  const res = await fetch(url, { headers: { apikey: SUPABASE_ANON } });
  if (!res.ok) throw new Error(`rewards http ${res.status}`);
  const rows = await res.json();
  // Normalise to the shape the sheet uses (title/cost/emoji) while keeping the raw fields.
  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    description: r.description || '',
    cost: r.cost_points,
    stock: r.stock,
    emoji: r.emoji || '🎁',
    category: r.category || '',
    raw: r
  }));
}

// Generate a unique client_key per attempt for idempotent redemption.
// UUID is cheap and every browser we target supports crypto.randomUUID().
function newClientKey() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  // Very-old-browser fallback — plenty of entropy for a per-tap key.
  return `ck-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Atomically redeem a reward. Returns the RPC payload on success:
 *   { ok, redemption_id, reward_id, reward_title, cost_points, status,
 *     balance_after, idempotent_replay }
 *
 * On business-rule failure the RPC raises a P0001/42501 with a stable message
 * ('insufficient_points' | 'reward_out_of_stock' | 'reward_inactive' |
 * 'reward_not_found' | 'not_authenticated' | 'profile_missing') — we surface
 * that via `err.code` so the UI can render a specific line without parsing
 * prose.
 *
 * `clientKey` is auto-generated per call; pass one explicitly only if the
 * caller has its own retry loop and wants to dedupe against it.
 */
export async function redeemReward(rewardId, { clientKey } = {}) {
  const key = clientKey || newClientKey();
  try {
    const data = await rpc('redeem_reward', { p_reward_id: rewardId, p_client_key: key });
    return data;
  } catch (err) {
    // rpc.js sets err.body from PostgREST. Extract the stable code.
    const msg = err?.body?.message || err?.message || '';
    const code = msg.split(':')[0].trim();
    const known = new Set([
      'insufficient_points', 'reward_out_of_stock', 'reward_inactive',
      'reward_not_found', 'not_authenticated', 'profile_missing'
    ]);
    const e = new Error(known.has(code) ? code : (msg || 'redemption_failed'));
    e.code = known.has(code) ? code : 'redemption_failed';
    e.status = err?.status;
    e.original = err;
    throw e;
  }
}

// History for "My Ihsan" — most recent 50 redemptions for the caller.
export async function myRedemptions() {
  try {
    const data = await rpc('my_redemptions', {});
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (err?.noauth) return [];
    return [];
  }
}

// Human-friendly copy for the codes redeemReward can throw.
export function redemptionErrorMessage(code, extra = {}) {
  switch (code) {
    case 'insufficient_points':
      return extra.needed
        ? `You need ${formatPoints(extra.needed)} more pts.`
        : 'Not enough Ihsan Points yet.';
    case 'reward_out_of_stock':
      return 'That reward just ran out. Try another one.';
    case 'reward_inactive':
      return 'That reward is not available right now.';
    case 'reward_not_found':
      return 'That reward is no longer listed.';
    case 'not_authenticated':
      return 'Please sign in to redeem.';
    case 'profile_missing':
      return 'Your profile is still setting up — try again in a moment.';
    default:
      return 'Redemption did not go through. Please try again.';
  }
}
